/**
 * User profile display – dashboard-wt (dark card, light text)
 */

import React from 'react';
import { useI18n } from '../../providers';
import Avatar from '../common/Avatar';
import StatusBadge from '../common/StatusBadge';
import Button from '../common/Button';

const UserProfile = ({ user, showActions = false, onEdit }) => {
  const { formatDate, language } = useI18n();

  if (!user) return null;

  const getVerificationVariant = (level) => {
    const variants = { verified: 'success', pending: 'warning', unverified: 'default' };
    return variants[level] || 'default';
  };

  const cardClass = 'rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#111] p-4';
  const textPrimary = 'text-[#fafafa]';
  const textMuted = 'text-[#a3a3a3]';

  return (
    <div className="space-y-6 animate-fadeInUp">
      <div className="flex items-center gap-4">
        <Avatar
          src={user.photoUrl}
          name={user.fullName || user.name || 'U'}
          size="xl"
          variant="circle"
        />
        <div className="flex-1">
          <h2 className={`text-2xl font-bold ${textPrimary} mb-1`}>
            {user.fullName || user.name || (language === 'fr' ? 'Utilisateur' : 'User')}
          </h2>
          {user.email && <p className={textMuted}>{user.email}</p>}
          {user.phone && <p className={`text-sm ${textMuted}`}>{user.phone}</p>}
        </div>
        {showActions && onEdit && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            {language === 'fr' ? 'Modifier' : 'Edit'}
          </Button>
        )}
      </div>

      {user.bio && (
        <div className={`${cardClass} border-l-4 border-l-[#22c55e]`}>
          <p className={textPrimary}>{user.bio}</p>
        </div>
      )}

      <div className={`${cardClass} bg-[rgba(34,197,94,0.06)] border-[rgba(34,197,94,0.2)]`}>
        <p className={`text-sm font-medium ${textMuted} mb-2`}>
          {language === 'fr' ? 'Statut de vérification' : 'Verification Status'}
        </p>
        <StatusBadge variant={getVerificationVariant(user.verificationLevel)} size="md">
          {user.verificationLevel === 'verified'
            ? language === 'fr' ? 'Vérifié' : 'Verified'
            : user.verificationLevel === 'pending'
            ? language === 'fr' ? 'En attente' : 'Pending'
            : language === 'fr' ? 'Non vérifié' : 'Unverified'}
        </StatusBadge>
      </div>

      {(user.stats || user.transactionCount !== undefined || user.rating !== undefined) && (
        <div className="grid grid-cols-3 gap-4">
          {user.transactionCount !== undefined && (
            <div className={`${cardClass} text-center`}>
              <div className="text-2xl font-bold text-[#22c55e]">{user.transactionCount || 0}</div>
              <div className={`text-xs ${textMuted} mt-1`}>{language === 'fr' ? 'Transactions' : 'Transactions'}</div>
            </div>
          )}
          {user.rating !== undefined && (
            <div className={`${cardClass} text-center`}>
              <div className="text-2xl font-bold text-[#eab308]">{user.rating?.toFixed(1) || '0.0'}</div>
              <div className={`text-xs ${textMuted} mt-1`}>{language === 'fr' ? 'Note' : 'Rating'}</div>
            </div>
          )}
          {user.reviewCount !== undefined && (
            <div className={`${cardClass} text-center`}>
              <div className="text-2xl font-bold text-[#a855f7]">{user.reviewCount || 0}</div>
              <div className={`text-xs ${textMuted} mt-1`}>{language === 'fr' ? 'Avis' : 'Reviews'}</div>
            </div>
          )}
        </div>
      )}

      {user.createdAt && (
        <div className="flex items-center justify-between text-sm">
          <span className={textMuted}>{language === 'fr' ? 'Membre depuis' : 'Member Since'}</span>
          <span className={`font-medium ${textPrimary}`}>{formatDate(user.createdAt)}</span>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
